// ─── Angular Imports ───────────────────────────────────────────────────────────
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';

// ─── Third-Party Imports ───────────────────────────────────────────────────────
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

// ─── Application Imports ───────────────────────────────────────────────────────
import { versions } from '../../../environments/versions';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { MenuService } from 'src/app/services/menu.service';
import { Permission } from 'src/app/constants/permission.constant';
import { IUserProfile } from 'src/app/models/users/userProfile';
import { PreferenceComponent } from './preference/preference.component';
import { IRouteLink, IPermissionGatedRoute } from 'src/app/models/route.model';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') matSidenav!: MatSidenav;

  // ─── State ───────────────────────────────────────────────────────────────────
  user: IUserProfile | null = null;
  isLoading = true;
  Permission = Permission;
  mode: MatDrawerMode = 'side';

  readonly apiBase = environment.apiBase;
  readonly version = versions.revision;
  readonly dateTime = versions.dateTime;

  private destroy$ = new Subject<void>();

  // ─── Navigation Links ────────────────────────────────────────────────────────
  private readonly allRoutes: readonly IPermissionGatedRoute[] = [
    { link: 'dashboard', name: 'Dashboard', icon: 'home' },
    { link: 'batches', name: 'Jobs', icon: 'format_list_bulleted', requires: Permission.BatchesRead },
    { link: 'collections', name: 'Collections', icon: 'collections', requires: Permission.OutputsCollectionsRead },
    { link: 'packages', name: 'Packages', icon: 'photo_camera', requires: Permission.PackagesRead },
    { link: 'settings', name: 'Settings', icon: 'lightbulb', requires: Permission.SettingsRead },
    { link: 'mastersuites', name: 'Master Suites', icon: 'style', requires: Permission.MasterSuitesRead },
    { link: 'organizations', name: 'Organizations', icon: 'business', requires: Permission.OrganizationsRead },
    { link: 'users', name: 'Users', icon: 'people_alt', requires: Permission.UsersRead },
    { link: 'admin/email-logs', name: 'Email logs', icon: 'mail', requires: Permission.EmailLogsRead },
  ];
  public routeLinks: IRouteLink[] = [];

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private dialog: MatDialog,
    public authService: AuthService,
    private signalrService: SignalrService,
    private menuService: MenuService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
  ) {}

  // ─── Lifecycle Hooks ────────────────────────────────────────────────────────
  ngOnInit() {
    this.setupAuth();
    this.setupResponsiveLayout();
    this.watchAvatarChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Setup Methods ──────────────────────────────────────────────────────────
  private setupAuth() {
    fetchAuthSession()
      .then((session) => {
        if (!session.tokens?.accessToken) {
          this.isLoading = false;
          return;
        }
        this.setUserInformation(session.tokens.accessToken);
        this.signalrService.connect(session.tokens.accessToken.toString());
      })
      .catch(() => {
        this.isLoading = false;
      });
  }

  private setupResponsiveLayout() {
    this.breakpointObserver
      .observe([`(max-width: 1650px)`])
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ matches }) => {
        this.mode = matches ? 'over' : 'side';
        if (matches) {
          this.matSidenav?.close();
        }
      });
  }

  private watchAvatarChanges() {
    this.authService.currentAvatar.pipe(takeUntil(this.destroy$)).subscribe((avatar: string) => {
      if (avatar && this.user) {
        this.user.profileImageUrl = avatar;
      }
    });
  }

  // ─── Logic Methods ──────────────────────────────────────────────────────────
  private setUserInformation(accessToken: any) {
    try {
      const cognitoGroups: string[] =
        (accessToken.payload['cognito:groups'] as string[]) || [];

      this.authService.setCognitoGroup(cognitoGroups);

      this.authService
        .getUserInformation()
        .pipe(takeUntil(this.destroy$))
        .subscribe((profile: IUserProfile) => {
          this.authService.setAuthInformation(profile);
          this.user = profile;
          this.authService.setDateFormat(profile.preference);
          this.buildPermissionBasedRoutes();
          this.isLoading = false;
        });
    } catch (error) {
      console.error('Error setting user info:', error);
    }
  }

  public buildPermissionBasedRoutes(): void {
    this.routeLinks = this.allRoutes.filter(
      (route) => !route.requires || this.authService.hasPermission(route.requires),
    );
  }

  // ─── Action Methods ─────────────────────────────────────────────────────────
  home() {
    this.router.navigate(['dashboard']);
  }

  onLogoutClick() {
    signOut({ global: true })
      .then(() => (this.user = null))
      .catch((err) => console.error('Logout error', err));
  }

  openMenu(event: boolean) {
    this.menuService.updateMenuStatus(event);
  }

  itemClicked() {
    if (this.mode === 'over') {
      this.matSidenav.close();
    }
  }

  openMyPreference() {
    const dialogRef = this.dialog.open(PreferenceComponent, {
      data: this.user,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.user = result;
      }
    });
  }
}
